import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { Button } from '@/components'
import { Dropdown } from '@/components/base/Dropdown'

import { useSelectionScope } from '../hooks/useSelectionScope'
import { ImageAttributeSelection, serializeImageAttributeFilters } from '../utils/imageFilters'

export function ImageAttributeFilters({
  value,
  onChange,
}: {
  value: ImageAttributeSelection
  onChange: (value: ImageAttributeSelection) => void
}) {
  const { t } = useTranslation()
  const scope = useSelectionScope()
  const [filterError, setFilterError] = useState<Error>()
  const {
    data = [],
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['image-attribute-filters', scope],
    queryFn: async ({ signal }) => {
      const response = await API.getImageAttributeFilters(undefined, { signal })
      if (response.code !== 0) throw new Error(response.msg)
      return response.filters ?? []
    },
    staleTime: 60_000,
    retry: false,
  })
  const labels: Record<string, string> = {
    agent_driver: t('create.imageView.deploymentType'),
    os_family: t('create.imageView.os'),
    cuda_version: t('create.imageView.cudaVersion'),
  }

  return (
    <div className="flex flex-wrap items-end gap-4" aria-busy={isPending}>
      {data.map((filter) => (
        <div
          key={filter.key}
          className="w-40"
          data-ai-component="image-attribute-filter"
          data-ai-value={filter.key}
        >
          <div className="t-sb text-secondary mb-2">{labels[filter.key] ?? filter.key}</div>
          <Dropdown
            multiple
            showAllOption
            allOptionLabel={t('common.any', 'Any')}
            value={value[filter.key]?.length ? value[filter.key] : ['all']}
            options={filter.values.map((option) => ({
              value: option.value,
              label: option.valueRaw,
            }))}
            onChange={(selected) => {
              const values = (Array.isArray(selected) ? selected : [selected]).filter(
                (item): item is number => typeof item === 'number',
              )
              const next = { ...value, [filter.key]: values }
              try {
                serializeImageAttributeFilters(next)
                setFilterError(undefined)
                onChange(next)
              } catch (error) {
                setFilterError(error as Error)
              }
            }}
          />
        </div>
      ))}
      {isError && (
        <Button type="outline" onClick={() => void refetch()}>
          {t('create.imageView.retryFilters')}
        </Button>
      )}
      {filterError && (
        <p className="w-full t-sm text-error" role="alert">
          {t('create.imageView.filterLimit')}
        </p>
      )}
    </div>
  )
}
